import {
  Avatar, Badge, Button, Card, DataTable, EmailInput, ErrorState, Field, Input, InvitationIssuedPanel, LoadingState,
  Notice, PageHeader, PhoneInput, Roles, Row, Select, Stack, usePermission, Permissions,
  useApi, useI18n, useRoleLabel,
  type InvitationIssuedResponse, type PermissionOptionResponse, type StaffResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { AccountRecoveryPanel } from '../components/AccountRecoveryPanel';
import { StaffEditor } from '../components/StaffEditor';
import { OFFERED_ROLES } from '../staffRoles';

/**
 * Staff administration (§4.11.4).
 *
 * People are invited rather than created with a password. The link goes to their phone by SMS and also
 * comes back here once, for the person inviting to copy if the text does not arrive; accepting it still
 * needs a code sent to the invitee's own phone. Someone who has not accepted can be sent a new link, which
 * stops every earlier one working (decisions 2026-09-23 #3, #8 and #16).
 */
export function Staff() {
  const api = useApi();
  const { t, formatPhone } = useI18n();
  const roleLabel = useRoleLabel();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<StaffResponse | null>(null);
  const [recovering, setRecovering] = useState<StaffResponse | null>(null);
  const [changing, setChanging] = useState<StaffResponse | null>(null);
  // A former colleague comes back by a new invitation, which reopens their membership (C4).
  const [reinviting, setReinviting] = useState<StaffResponse | null>(null);
  const [issued, setIssued] = useState<InvitationIssuedResponse | null>(null);

  const canInvite = usePermission(Permissions.staff.invite);
  const canManagePermissions = usePermission(Permissions.staff.managePermissions);
  const canDeactivate = usePermission(Permissions.staff.deactivate);
  const canUpdate = usePermission(Permissions.staff.update);
  const canRecover = usePermission(Permissions.accounts.recover);

  const queryClient = useQueryClient();
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.staff.list() });

  const deactivate = useMutation({
    mutationFn: (membershipId: number) => api.staff.deactivate(membershipId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });

  const resend = useMutation({
    mutationFn: (membershipId: number) => api.staff.resendInvitation(membershipId),
    onSuccess: (invitation) => setIssued(invitation),
  });

  if (editing && canManagePermissions) {
    return <PermissionsEditor member={editing} onClose={() => setEditing(null)} />;
  }

  if (changing && canUpdate) {
    return <StaffEditor member={changing} onClose={() => setChanging(null)} />;
  }

  return (
    <Stack>
      <PageHeader
        title={t('staff.title')}
        actions={canInvite
          ? <Button onClick={() => { setIssued(null); setReinviting(null); setInviting(true); }}>{t('staff.invite')}</Button>
          : undefined}
      />

      {issued && <InvitationIssuedPanel invitation={issued} onDismiss={() => setIssued(null)} />}

      {(inviting || reinviting) && (
        <InviteForm
          key={reinviting?.membershipId ?? 'new'}
          former={reinviting}
          onCancel={() => { setInviting(false); setReinviting(null); }}
          onIssued={(invitation) => { setIssued(invitation); setInviting(false); setReinviting(null); }}
        />
      )}

      {recovering && (
        <AccountRecoveryPanel
          membershipId={recovering.membershipId}
          fullName={recovering.fullName}
          onClose={() => setRecovering(null)}
        />
      )}

      {deactivate.isError && <ErrorState error={deactivate.error} />}
      {resend.isError && <ErrorState error={resend.error} />}

      <Card flush>
        <DataTable
          caption={t('staff.title')}
          rows={staff.data}
          rowKey={(member) => member.membershipId}
          isLoading={staff.isPending}
          error={staff.error}
          onRetry={() => void staff.refetch()}
          empty={t('staff.empty')}
          columns={[
            {
              key: 'name',
              header: t('staff.name'),
              render: (m) => (
                <Row>
                  <Avatar name={m.fullName} photoUrl={m.photoUrl} size="sm" />
                  <span>{m.fullName ?? '—'}</span>
                </Row>
              ),
            },
            {
              key: 'phone',
              header: t('staff.phone'),
              // A released account has no number; its history stays under its name (Q25).
              render: (m) => m.numberReleased
                ? <Badge tone="neutral">{t('people.numberReleased')}</Badge>
                : formatPhone(m.phone),
            },
            { key: 'role', header: t('staff.role'), render: (m) => roleLabel(m.roleKey) ?? m.roleNameAr ?? '—' },
            { key: 'job', header: t('staff.jobTitle'), render: (m) => m.jobTitle ?? '—' },
            {
              key: 'classes',
              header: t('staff.classes'),
              render: (m) => m.classes.length === 0
                ? '—'
                : m.classes.map((assignment) => assignment.className).join('، '),
            },
            {
              key: 'status',
              header: t('audit.action'),
              render: (m) => (
                <Badge tone={m.status === 'Active' ? 'success' : m.status === 'Invited' ? 'info' : 'neutral'}>
                  {t(`status.${m.status}`)}
                </Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (m) => (
                <Row>
                  {canUpdate && m.status !== 'Deactivated' && (
                    <Button variant="ghost" onClick={() => setChanging(m)}>{t('action.edit')}</Button>
                  )}
                  {canInvite && m.status === 'Deactivated' && !m.numberReleased && (
                    <Button variant="ghost" onClick={() => { setIssued(null); setInviting(false); setReinviting(m); }}>
                      {t('staff.inviteAgain')}
                    </Button>
                  )}
                  {canManagePermissions && (
                    <Button variant="ghost" onClick={() => setEditing(m)}>{t('staff.permissions')}</Button>
                  )}
                  {canRecover && !m.numberReleased && (
                    <Button variant="ghost" onClick={() => setRecovering(m)}>{t('accountRecovery.action')}</Button>
                  )}
                  {canInvite && m.status === 'Invited' && (
                    <Button
                      variant="ghost"
                      busy={resend.isPending && resend.variables === m.membershipId}
                      onClick={() => resend.mutate(m.membershipId)}
                    >
                      {t('invitation.resend')}
                    </Button>
                  )}
                  {canDeactivate && m.status !== 'Deactivated' && (
                    <Button
                      variant="ghost"
                      busy={deactivate.isPending && deactivate.variables === m.membershipId}
                      onClick={() => deactivate.mutate(m.membershipId)}
                    >
                      {t('staff.deactivate')}
                    </Button>
                  )}
                </Row>
              ),
            },
          ]}
        />
      </Card>
    </Stack>
  );
}

function InviteForm({ former, onCancel, onIssued }: {
  /** A deactivated colleague invited again: the form starts from what was on record (C4). */
  former?: StaffResponse | null;
  onCancel: () => void; onIssued: (invitation: InvitationIssuedResponse) => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState(former?.fullName ?? '');
  const [phone, setPhone] = useState(former?.phone ?? '');
  const [roleKey, setRoleKey] = useState<string>(
    former?.roleKey && OFFERED_ROLES.includes(former.roleKey) ? former.roleKey : Roles.tenant.teacher);
  const [jobTitle, setJobTitle] = useState(former?.jobTitle ?? '');
  const [email, setEmail] = useState('');
  const [classIds, setClassIds] = useState<number[]>([]);

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });

  const invite = useMutation({
    mutationFn: () => api.staff.invite({
      fullName,
      phone,
      roleKey,
      jobTitle: jobTitle || null,
      classIds: classIds.length > 0 ? classIds : null,
      email: email.trim() || null,
    }),
    onSuccess: async (invitation) => {
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
      onIssued(invitation);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (fullName.trim() && phone.trim()) invite.mutate();
  };

  return (
    <Card title={former ? t('staff.inviteAgainOf', { name: former.fullName ?? '' }) : t('staff.invite')}>
      <form onSubmit={submit}>
        <Stack>
          {former && <Notice tone="info">{t('staff.inviteAgain.hint')}</Notice>}
          {invite.isError && <ErrorState error={invite.error} />}

          <Field label={t('staff.name')} required>
            {(props) => (
              <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} required />
            )}
          </Field>

          <Field label={t('staff.phone')} hint={t('auth.phone.hint')} required>
            {(props) => (
              <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} required />
            )}
          </Field>

          <Field label={t('invitation.email')} hint={t('invitation.email.hint')}>
            {(props) => (
              <EmailInput
                {...props}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label={t('staff.role')} required>
            {(props) => (
              <Select {...props} value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
                {OFFERED_ROLES.map((role) => (
                  <option key={role} value={role}>{t(`role.${role}`)}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={t('staff.jobTitle')}>
            {(props) => (
              <Input {...props} value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} />
            )}
          </Field>

          {/* A group of choices rather than one control, so a fieldset and a legend — which is what a
              screen reader announces as "staff classes, group" before reading the boxes. */}
          <fieldset className="dg-field">
            <legend className="dg-field__label">{t('staff.classes')}</legend>
            <Stack tight>
              {(classes.data?.items ?? []).map((klass) => (
                <label key={klass.id} className="dg-checkbox">
                  <input
                    type="checkbox"
                    checked={classIds.includes(klass.id)}
                    onChange={(event) => setClassIds((current) => event.target.checked
                      ? [...current, klass.id]
                      : current.filter((id) => id !== klass.id))}
                  />
                  <span>{klass.name}</span>
                </label>
              ))}
            </Stack>
          </fieldset>

          <Row>
            <Button type="submit" busy={invite.isPending} busyLabel={t('action.saving')}>
              {t('staff.invite')}
            </Button>
            <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * One person's permissions (FR-ADM-14, FR-RBAC-05).
 *
 * This is the screen the client asked for directly: tailoring one teacher's capabilities without inventing
 * a role for them. A combination the hard constraints forbid is shown disabled rather than hidden, so an
 * owner can see that the rule exists instead of wondering where the option went.
 */
function PermissionsEditor({ member, onClose }: { member: StaffResponse; onClose: () => void }) {
  const api = useApi();
  const { t } = useI18n();
  const roleLabel = useRoleLabel();
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState<Record<string, boolean>>(
    Object.fromEntries(member.permissionOverrides.map((o) => [o.permissionKey, o.granted])),
  );

  const options = useQuery({
    queryKey: ['staff', member.membershipId, 'permissions'],
    queryFn: () => api.staff.permissions(member.membershipId),
  });

  const save = useMutation({
    mutationFn: () => api.staff.setPermissions(
      member.membershipId,
      Object.entries(overrides).map(([permissionKey, granted]) => ({ permissionKey, granted })),
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
      onClose();
    },
  });

  if (options.isPending) return <LoadingState />;
  if (options.isError) return <ErrorState error={options.error} onRetry={() => void options.refetch()} />;

  const groups = new Map<string, PermissionOptionResponse[]>();
  for (const option of options.data) {
    const list = groups.get(option.group) ?? [];
    list.push(option);
    groups.set(option.group, list);
  }

  const effective = (option: PermissionOptionResponse) =>
    overrides[option.key] ?? option.fromRole;

  return (
    <Stack>
      <PageHeader
        title={`${t('staff.permissions')} — ${member.fullName ?? ''}`}
        lede={roleLabel(member.roleKey) ?? member.roleNameAr ?? undefined}
      />

      {save.isError && <ErrorState error={save.error} />}

      {[...groups].map(([group, list]) => (
        <Card key={group} title={group}>
          <Stack tight>
            {list.map((option) => (
              <label key={option.key} className="dg-checkbox">
                <input
                  type="checkbox"
                  checked={option.grantable && effective(option)}
                  disabled={!option.grantable}
                  onChange={(event) => setOverrides((current) => ({
                    ...current, [option.key]: event.target.checked,
                  }))}
                />
                <span>
                  {option.key}
                  {option.fromRole && (
                    <span className="dg-muted"> · {t('staff.permissionFromRole')}</span>
                  )}
                  {!option.grantable && (
                    <span className="dg-muted"> · {t('staff.permissionBlocked')}</span>
                  )}
                </span>
              </label>
            ))}
          </Stack>
        </Card>
      ))}

      <Row>
        <Button busy={save.isPending} busyLabel={t('action.saving')} onClick={() => save.mutate()}>
          {t('action.save')}
        </Button>
        <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
      </Row>
    </Stack>
  );
}
